// features/pos/CustomerDisplay.tsx
/**
 * Customer Display Screen (CSD)
 * 
 * Three-stage state machine:
 * Stage 1: Idle - Promo carousel with ads/specials
 * Stage 2: Ordering - Animated itemized receipt with glowing total
 * Stage 3: Payment - QR code or tap-to-pay animation
 * 
 * Designed for second monitor at /pos/display
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { usePosStore } from '@/store/posStore';
import { Spinner } from '@/components/ui/Spinner';
import {
    ShoppingCart, Sparkles, QrCode, CreditCard, Smartphone,
    Clock, CheckCircle, ChefHat, Package, Star, Gift,
    Coffee, Pizza, IceCream, Beef, Croissant, Wine,
    ChevronRight, ArrowUp, TrendingUp,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

type DisplayStage = 'idle' | 'ordering' | 'payment';

interface PromoItem {
    id: string;
    title: string;
    subtitle: string;
    icon: React.ElementType;
    color: string;
    bgGradient: string;
}

// ============================================================
// PROMO DATA
// ============================================================

const PROMOS: PromoItem[] = [
    {
        id: '1', title: 'Happy Hour!', subtitle: '20% off all drinks 4-6PM',
        icon: Coffee, color: 'text-amber-400', bgGradient: 'from-amber-500/20 to-orange-500/10',
    },
    {
        id: '2', title: 'Loyalty Rewards', subtitle: 'Earn 1 point per ₦100 spent',
        icon: Star, color: 'text-purple-400', bgGradient: 'from-purple-500/20 to-indigo-500/10',
    },
    {
        id: '3', title: 'Combo Deal', subtitle: 'Burger + Fries + Drink = ₦2,500',
        icon: Pizza, color: 'text-rose-400', bgGradient: 'from-rose-500/20 to-pink-500/10',
    },
    {
        id: '4', title: 'New Arrivals', subtitle: 'Check out our fresh bakery items',
        icon: Croissant, color: 'text-emerald-400', bgGradient: 'from-emerald-500/20 to-teal-500/10',
    },
    {
        id: '5', title: 'Weekend Special', subtitle: 'Family platter at 30% off',
        icon: Gift, color: 'text-blue-400', bgGradient: 'from-blue-500/20 to-cyan-500/10',
    },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function CustomerDisplay() {
    const pos = usePosStore();
    const [stage, setStage] = useState<DisplayStage>('idle');
    const [currentPromo, setCurrentPromo] = useState(0);
    const [showPaymentQR, setShowPaymentQR] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Determine stage based on cart state
    useEffect(() => {
        if (pos.isPaymentModalOpen) {
            setStage('payment');
        } else if (pos.cart.length > 0) {
            setStage('ordering');
        } else {
            setStage('idle');
        }
    }, [pos.cart.length, pos.isPaymentModalOpen]);

    // Promo carousel rotation (every 5 seconds)
    useEffect(() => {
        if (stage !== 'idle') return;
        const interval = setInterval(() => {
            setCurrentPromo(prev => (prev + 1) % PROMOS.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [stage]);

    // Play sound when item is added
    useEffect(() => {
        if (pos.cart.length > 0 && stage === 'ordering') {
            try {
                if (!audioRef.current) {
                    audioRef.current = new Audio('/sounds/item-added.mp3');
                }
                audioRef.current.play().catch(() => { });
            } catch { }
        }
    }, [pos.cart.length]);

    const formatNaira = (val: number) => `₦${(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatPoints = (val: number) => val?.toLocaleString() || '0';

    const promo = PROMOS[currentPromo];

    return (
        <div className="h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white overflow-hidden">
            {/* ========== STAGE 1: IDLE - PROMO CAROUSEL ========== */}
            <AnimatePresence mode="wait">
                {stage === 'idle' && (
                    <motion.div
                        key="idle"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="h-full flex flex-col"
                    >
                        {/* Header */}
                        <div className="px-8 py-6 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                                    <ShoppingCart size={20} className="text-white" />
                                </div>
                                <div>
                                    <h1 className="text-lg font-bold">Welcome!</h1>
                                    <p className="text-sm text-slate-400">Scan items to begin</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Clock size={14} />
                                {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>

                        {/* Promo Carousel */}
                        <div className="flex-1 flex items-center justify-center p-8">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={promo.id}
                                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                                    className="text-center max-w-lg"
                                >
                                    <div className={clsx(
                                        'w-32 h-32 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl',
                                        'bg-gradient-to-br', promo.bgGradient
                                    )}>
                                        <promo.icon size={64} className={promo.color} />
                                    </div>
                                    <motion.h2
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="text-4xl font-black mb-4"
                                    >
                                        {promo.title}
                                    </motion.h2>
                                    <motion.p
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 }}
                                        className="text-xl text-slate-400"
                                    >
                                        {promo.subtitle}
                                    </motion.p>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Carousel Dots */}
                        <div className="flex items-center justify-center gap-2 pb-8">
                            {PROMOS.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentPromo(idx)}
                                    className={clsx(
                                        'w-2 h-2 rounded-full transition-all duration-300',
                                        idx === currentPromo ? 'bg-emerald-500 w-8' : 'bg-slate-600 hover:bg-slate-500'
                                    )}
                                    type="button"
                                />
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="px-8 py-4 text-center text-xs text-slate-600">
                            <Sparkles size={12} className="inline mr-1" />
                            Thank you for shopping with us!
                        </div>
                    </motion.div>
                )}

                {/* ========== STAGE 2: ORDERING - ITEMIZED RECEIPT ========== */}
                {stage === 'ordering' && (
                    <motion.div
                        key="ordering"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="h-full flex flex-col"
                    >
                        {/* Header */}
                        <div className="px-8 py-5 border-b border-slate-800 flex items-center justify-between flex-shrink-0">
                            <div>
                                <h1 className="text-lg font-bold">Your Order</h1>
                                <p className="text-sm text-slate-400">{pos.cart.length} item{pos.cart.length !== 1 ? 's' : ''}</p>
                            </div>
                            {pos.customerInfo && (
                                <div className="text-right">
                                    <p className="text-sm font-medium">{pos.customerInfo.name}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-xs text-amber-400">⭐ {formatPoints(pos.customerInfo.loyaltyPoints)} pts</span>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-bold">
                                            {pos.customerInfo.loyaltyTier?.toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Item List */}
                        <div className="flex-1 overflow-y-auto px-8 py-4 space-y-1">
                            <AnimatePresence>
                                {pos.cart.map((item, idx) => (
                                    <motion.div
                                        key={item.id}
                                        initial={{ opacity: 0, x: -30, height: 0 }}
                                        animate={{ opacity: 1, x: 0, height: 'auto' }}
                                        exit={{ opacity: 0, x: 30, height: 0 }}
                                        transition={{ delay: idx * 0.1, type: 'spring', stiffness: 200, damping: 20 }}
                                        className="flex items-center justify-between py-3 border-b border-slate-800/50"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                                                {item.imageUrl ? (
                                                    <img src={item.imageUrl} alt="" className="w-full h-full rounded-xl object-cover" />
                                                ) : (
                                                    <Package size={18} className="text-slate-500" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold">{item.productName}</p>
                                                <p className="text-xs text-slate-500">
                                                    {formatNaira(item.unitPrice)} × {item.quantity}
                                                </p>
                                            </div>
                                        </div>
                                        <motion.p
                                            key={`price-${item.id}-${item.totalAmount}`}
                                            initial={{ scale: 1.5, color: '#34d399' }}
                                            animate={{ scale: 1, color: '#e2e8f0' }}
                                            className="text-sm font-bold tabular-nums"
                                        >
                                            {formatNaira(item.totalAmount)}
                                        </motion.p>
                                    </motion.div>
                                ))}
                            </AnimatePresence>

                            {/* Animated subtotal updates */}
                            <motion.div
                                key={`totals-${pos.grandTotal}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mt-4 pt-4 border-t-2 border-slate-700 space-y-2"
                            >
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">Subtotal</span>
                                    <span>{formatNaira(pos.subtotal)}</span>
                                </div>
                                {pos.discountTotal > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-amber-400">Discount</span>
                                        <span className="text-amber-400">-{formatNaira(pos.discountTotal)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">Tax (7.5%)</span>
                                    <span>{formatNaira(pos.taxTotal)}</span>
                                </div>
                            </motion.div>
                        </div>

                        {/* Glowing Total */}
                        <div className="px-8 py-6 border-t-2 border-slate-700 flex-shrink-0">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-lg font-bold text-slate-400">TOTAL</span>
                                <motion.span
                                    key={`grand-${pos.grandTotal}`}
                                    initial={{ scale: 1.3 }}
                                    animate={{ scale: 1 }}
                                    className="text-5xl font-black text-emerald-400 tabular-nums"
                                    style={{
                                        textShadow: '0 0 40px rgba(52, 211, 153, 0.5), 0 0 80px rgba(52, 211, 153, 0.3)',
                                    }}
                                >
                                    {formatNaira(pos.grandTotal)}
                                </motion.span>
                            </div>
                            {pos.customerInfo && (
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <Star size={12} className="text-amber-400" />
                                    You'll earn {Math.floor(pos.grandTotal / 100)} loyalty points!
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {/* ========== STAGE 3: PAYMENT ========== */}
                {stage === 'payment' && (
                    <motion.div
                        key="payment"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="h-full flex flex-col items-center justify-center p-8"
                    >
                        {!showPaymentQR ? (
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="text-center"
                            >
                                <div className="w-24 h-24 rounded-3xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-8 animate-pulse">
                                    <CreditCard size={48} className="text-emerald-400" />
                                </div>
                                <h2 className="text-3xl font-black mb-2">Processing Payment</h2>
                                <p className="text-lg text-slate-400 mb-8">Please wait...</p>
                                <div className="flex items-center justify-center gap-2">
                                    <motion.div
                                        animate={{ scale: [1, 1.5, 1] }}
                                        transition={{ duration: 1.5, repeat: Infinity }}
                                        className="w-3 h-3 rounded-full bg-emerald-500"
                                    />
                                    <motion.div
                                        animate={{ scale: [1, 1.5, 1] }}
                                        transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                                        className="w-3 h-3 rounded-full bg-emerald-500"
                                    />
                                    <motion.div
                                        animate={{ scale: [1, 1.5, 1] }}
                                        transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
                                        className="w-3 h-3 rounded-full bg-emerald-500"
                                    />
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="text-center"
                            >
                                <div className="w-48 h-48 bg-white rounded-3xl flex items-center justify-center mx-auto mb-8 p-4 shadow-2xl">
                                    <QrCode size={160} className="text-slate-900" />
                                </div>
                                <h2 className="text-2xl font-black mb-2">Scan to Pay</h2>
                                <p className="text-slate-400">or tap your card below</p>
                            </motion.div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}